import {
  afterNextRender,
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
  input,
  ElementRef,
  ViewChild,
  SecurityContext,
  signal,
  computed,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { CopilotFacadeService } from '../../services/copilot-facade.service';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, switchMap, of, map } from 'rxjs';
import { tap } from 'rxjs/operators';
import { trigger, transition, style, animate } from '@angular/animations';
import { Splitter } from 'primeng/splitter';
import { Button } from 'primeng/button';
import { MarkdownModule, SECURITY_CONTEXT } from 'ngx-markdown';
import {
  AuthService,
  OktaUserCacheService,
  OktaUserInfo,
} from '@brightside/core/auth';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import { CopilotPlaybooksPanelComponent } from '../copilot-playbooks-panel/copilot-playbooks-panel.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { CopilotActions } from '../../store/copilot.actions';
import {
  CopilotContextChip,
  CopilotContextComposerService,
} from '../../services/copilot-context-composer.service';

@Component({
  selector: 'lib-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Splitter,
    MarkdownModule,
    Button,
    ChatMessageComponent,
    CopilotPlaybooksPanelComponent,
  ],
  providers: [
    {
      provide: SECURITY_CONTEXT,
      useValue: SecurityContext.HTML,
    },
  ],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  animations: [
    trigger('messageAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(8px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComponent {
  /** When true, hides the chat input panel (e.g. conversation/employee layouts). */
  readonly disableChat = input<boolean>(false);

  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  readonly facade = inject(CopilotFacadeService);
  readonly auth = inject(AuthService);
  readonly oktaUserCache = inject(OktaUserCacheService);
  readonly composer = inject(CopilotContextComposerService);

  readonly messages$ = this.facade.messagesForSelectedConversation$;

  readonly selectedClientId$ = this.facade.selectedClientId$;
  readonly selectedConversationId$ = this.facade.selectedConversation$;

  readonly selectedClientId = toSignal(this.selectedClientId$);
  readonly selectedConversationId = toSignal(this.selectedConversationId$);

  readonly contextChips = this.composer.chips;
  readonly draftText = this.composer.draftText;
  readonly hasSendableContent = this.composer.hasSendableContent;
  readonly maxContextItems = this.composer.maxContextItems;
  readonly contextCount = computed(() => this.contextChips().length);

  readonly copilotName = 'BrightAI';
  readonly userDisplayName = signal('User');
  readonly userFullName = signal('Brightside User');
  readonly loggedInUserId = signal('');
  readonly disableInput = signal(false);
  readonly isComposing = signal(false);

  // Streaming signal for playbooks panel
  readonly isStreamingSignal = toSignal(
    this.selectedConversationId$.pipe(
      switchMap((id) => (id ? this.facade.isStreaming$(id) : of(false)))
    ),
    { initialValue: false }
  );

  readonly isStreaming$: Observable<boolean> = this.selectedConversationId$.pipe(
    switchMap((conversationId) =>
      conversationId ? this.facade.isStreaming$(conversationId) : of(false)
    ),
    tap((streaming) => {
      this.disableInput.set(streaming);
      this.scrollToBottom();
    })
  );

  readonly showThinking$ = combineLatest([this.isStreaming$, this.messages$]).pipe(
    map(([isStreaming, messages]) => {
      if (!isStreaming) return false;
      const lastAssistant = [...messages]
        .reverse()
        .find((m) => m.role === 'assistant' && m.metadata?.finalized === false);
      return !lastAssistant || !lastAssistant.content?.trim();
    })
  );

  readonly viewMessages$ = this.messages$.pipe(
    map((messages) =>
      messages.map((message) => {
        const user = this.oktaUserCache.getUser(message.owner_id ?? '');

        return {
          ...message,
          userDisplayName: user?.firstName ?? '',
          userInitials: this._getInitials(user),
        };
      })
    )
  );

  @ViewChild('scrollPanel') scrollPanelRef?: ElementRef<HTMLElement>;
  forceDisableInput = signal(false);
  forceHideInput = signal(false);

  constructor() {
    // Scroll to bottom on initial render so existing history is at the bottom on load.
    afterNextRender(() => this.scrollToBottom('auto'));

    this.messages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        setTimeout(() => this.scrollToBottom());
      });

    this.auth.getUser().then((user) => {
      if (user) {
        this.userDisplayName.set(user.given_name || 'User');
        this.userFullName.set(`${user.given_name} ${user.family_name}`);
        this.loggedInUserId.set(user.sub);
      }
    });

    effect(() => {
      this.forceDisableInput.set(this.facade.disableInput());
    });

    effect(() => {
      this.forceHideInput.set(this.disableChat() || this.facade.hideInput());
    });
  }

  send(): void {
    const content = this.composer.compiledMessageText().trim();
    if (!content) return;

    const clientId = this.selectedClientId();
    const conversationId = this.selectedConversationId();

    if (clientId && conversationId) {
      this.facade.sendMessage(
        clientId,
        conversationId,
        content,
        undefined,
        this.loggedInUserId()
      );

      // Clear draft + context after successful submit dispatch.
      this.composer.clear();
      this.store.dispatch(CopilotActions.clearInputText({ clientId, conversationId }));
      this.scrollToBottom();
    }
  }

  onDraftChange(value: string): void {
    this.composer.setDraftText(value);
  }

  handleComposerKeydown(event: KeyboardEvent, selectedConversationId: string): void {
    if (event.isComposing || this.isComposing() || event.key === 'Process') {
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (this.hasSendableContent() && selectedConversationId && !this.disableInput()) {
        this.send();
      }
    }
  }

  onComposerCompositionStart(): void {
    this.isComposing.set(true);
  }

  onComposerCompositionEnd(): void {
    this.isComposing.set(false);
  }

  removeContextChip(chipId: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.composer.removeContext(chipId);
  }

  trackContextChip(_: number, chip: CopilotContextChip): string {
    return chip.id;
  }

  contextChipIcon(sourceType: string): string {
    switch (sourceType) {
      case 'client-chat':
        return 'pi pi-comments';
      case 'actions':
      case 'fa-actions':
        return 'pi pi-check-square';
      case 'ai-insights':
      case 'insights':
        return 'pi pi-microchip-ai';
      case 'file':
        return 'pi pi-file';
      case 'image':
        return 'pi pi-image';
      default:
        return 'pi pi-tag';
    }
  }

  scrollToBottom(behavior: 'smooth' | 'auto' = 'smooth'): void {
    setTimeout(() => {
      const el = this.scrollPanelRef?.nativeElement;
      if (el) {
        if (typeof el.scrollTo === 'function') {
          el.scrollTo({ top: el.scrollHeight, behavior });
        } else {
          el.scrollTop = el.scrollHeight;
        }
      }
    }, 100);
  }

  private _getInitials(user?: OktaUserInfo): string {
    if (user) {
      return `${user.firstName.slice(0, 1)}${user.lastName.slice(0, 1)}`;
    }
    return 'BU';
  }
}
